import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: 2rem 1rem;
`;

export const Content = styled.div`
  width: 100%;
  max-width: 520px;
  animation: ${fadeIn} 0.3s ease;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 0.875rem;
  padding: 0;
  margin-bottom: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    opacity: 0.8;
  }
`;

export const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

export const StepDot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  transition: background 0.3s;
`;

export const Card = styled.div`
  background: ${props => props.theme.colors.contentBackground};
  border-radius: 0.75rem;
  box-shadow: ${props => props.theme.styles.smallShadow};
  overflow: hidden;
  animation: ${fadeIn} 0.25s ease;
`;

export const CardHeader = styled.div`
  padding: 2rem 2rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

export const CardBody = styled.div`
  padding: 1.5rem 2rem 2rem;

  ul {
    list-style: none;
    margin-bottom: 1.5rem;

    li {
      padding: 0.5rem 0;
      padding-left: 1.5rem;
      position: relative;
      color: ${props => props.theme.colors.text};
      font-size: 0.9375rem;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: ${props => props.theme.colors.primary};
        font-weight: 700;
      }
    }
  }
`;

export const StepTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

export const StepSubtitle = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.muted};
  line-height: 1.5;
`;

export const MetaLogo = styled.div`
  width: 3rem;
  height: 3rem;
  background: #1877f2;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: #fff;

  svg {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

export const MetaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: #1877f2;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PhoneList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const PhoneItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.main};
  border: 1.5px solid ${props => props.theme.colors.border};
  border-radius: 0.5rem;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.hover};
  }
`;

export const PhoneItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  strong {
    font-size: 0.9375rem;
    color: ${props => props.theme.colors.text};
  }

  span {
    font-size: 0.875rem;
    color: ${props => props.theme.colors.muted};
  }

  small {
    font-size: 0.8125rem;
    color: ${props => props.theme.colors.muted};
  }
`;

const badgeColors: Record<string, string> = {
  GREEN: '#45df40',
  YELLOW: '#f58f0e',
  RED: '#df4740',
};

export const PhoneItemBadge = styled.span<{ quality: string }>`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: ${props => badgeColors[props.quality] ?? props.theme.colors.muted};
  margin-left: 0.25rem;
`;

export const QrCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  background: ${props => props.theme.colors.main};
  border-radius: 0.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  min-height: 220px;
`;

export const QrCodeImage = styled.img`
  max-width: 200px;
  width: 100%;
  height: auto;
  border-radius: 0.25rem;
`;

export const QrCodeInstructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

export const QrCodeStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const QrCodeStepNumber = styled.div`
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.buttonText};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
`;

export const QrCodeStepText = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.text};
  line-height: 1.75rem;

  strong {
    font-weight: 600;
  }
`;

export const SuccessIcon = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: ${props => props.theme.colors.success};
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

export const ConnectedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.theme.colors.main};
  border: 1.5px solid ${props => props.theme.colors.border};
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    strong {
      font-size: 0.9375rem;
      color: ${props => props.theme.colors.text};
    }

    span {
      font-size: 0.875rem;
      color: ${props => props.theme.colors.muted};
    }

    small {
      font-size: 0.8125rem;
      color: ${props => props.theme.colors.muted};
      margin-top: 0.25rem;
    }
  }
`;

export const DisconnectButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: 1.5px solid ${props => props.theme.colors.danger};
  color: ${props => props.theme.colors.danger};
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${props => props.theme.colors.danger};
    color: #fff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Spinner = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid ${props => props.theme.colors.border};
  border-top-color: ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 2rem auto;
`;

export const ErrorMessage = styled.div`
  background: ${props => props.theme.colors.danger}22;
  border: 1px solid ${props => props.theme.colors.danger}44;
  color: ${props => props.theme.colors.danger};
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;
